import React, { useState } from 'react';
import { useScreenInit } from '../useScreenInit.js';
import { Shield, Clock, ChevronLeft, ChevronRight, CheckCircle2, Cloud, Lock, Star, HelpCircle, X } from 'lucide-react';
type RatingType = '5-point' | '10-point' | 'multiple-choice' | 'open-text';
type Question = {
  id: string;
  text: string;
  competency: string;
  type: RatingType;
  options?: string[];
};
// Questions grouped into pages
const QUESTION_PAGES: {
  competency: string;
  questions: Question[];
}[] = [{
  competency: 'Strategic Thinking',
  questions: [{
    id: 'q1',
    text: 'Sets a clear and compelling vision that aligns the team.',
    competency: 'Strategic Thinking',
    type: '5-point'
  }, {
    id: 'q2',
    text: 'Anticipates future challenges and plans accordingly.',
    competency: 'Strategic Thinking',
    type: '5-point'
  }]
}, {
  competency: 'Communication',
  questions: [{
    id: 'q3',
    text: 'On a scale of 1–10, how effectively does this leader communicate expectations?',
    competency: 'Communication',
    type: '10-point'
  }, {
    id: 'q4',
    text: "Which best describes this leader's communication style?",
    competency: 'Communication',
    type: 'multiple-choice',
    options: ['Direct & concise', 'Collaborative', 'Detailed & thorough', 'Inspirational']
  }]
}, {
  competency: 'Emotional Intelligence',
  questions: [{
    id: 'q5',
    text: "Demonstrates empathy and awareness of others' perspectives.",
    competency: 'Emotional Intelligence',
    type: '5-point'
  }, {
    id: 'q6',
    text: 'What is one strength this leader should continue leveraging?',
    competency: 'Emotional Intelligence',
    type: 'open-text'
  }]
}];
const SCALE_5 = ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'];
// Step model: 0 Welcome, 1 Instructions, 2..(2+pages-1) Questions, last Completion
const TOTAL_QUESTION_PAGES = QUESTION_PAGES.length;
const FIRST_QUESTION_STEP = 2;
const COMPLETION_STEP = FIRST_QUESTION_STEP + TOTAL_QUESTION_PAGES;
function QuestionInput({
  q,
  value,
  onChange
}: {
  q: Question;
  value: any;
  onChange: (v: any) => void;
}) {
  if (q.type === '5-point') {
    return <div className="grid grid-cols-5 gap-2" data-id="element-359" data-mp="wrhc8l40ezjn">
        {SCALE_5.map((label, i) => {
        const n = i + 1;
        const selected = value === n;
        return <button key={n} onClick={() => onChange(n)} className={`flex flex-col items-center justify-start gap-2 p-3 rounded-lg border text-center transition-all ${selected ? 'border-slate-900 bg-slate-50 ring-1 ring-slate-900' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-360" data-mp="15kkvxb5908mq" data-mpi={i}>
              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${selected ? 'bg-slate-900 text-white' : 'bg-white border border-slate-300 text-slate-600'}`} data-id="element-361" data-mp="8zkchsdu3xyw">
                {n}
              </span>
              <span className="text-[11px] leading-tight text-slate-500" data-id="element-362" data-mp="3l69cxznp88zx">
                {label}
              </span>
            </button>;
      })}
      </div>;
  }
  if (q.type === '10-point') {
    return <div className="flex flex-wrap gap-2" data-id="element-363" data-mp="hm4vln1h6cen">
        {Array.from({
        length: 10
      }, (_, i) => i + 1).map(n => {
        const selected = value === n;
        return <button key={n} onClick={() => onChange(n)} className={`w-10 h-10 rounded-full border text-sm font-medium transition-all ${selected ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 bg-white text-slate-600 hover:border-slate-400'}`} data-id="element-364" data-mp="coux7hw3yqeo">
              {n}
            </button>;
      })}
      </div>;
  }
  if (q.type === 'multiple-choice') {
    return <div className="space-y-2" data-id="element-365" data-mp="38u74z8717edk">
        {(q.options || []).map(opt => {
        const selected = value === opt;
        return <button key={opt} onClick={() => onChange(opt)} className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left text-sm font-medium transition-all ${selected ? 'border-slate-900 bg-slate-50 ring-1 ring-slate-900' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-366" data-mp="2yxlzymv0i39q">
              <span className={`w-4 h-4 rounded-full border flex items-center justify-center ${selected ? 'border-slate-900' : 'border-slate-300'}`} data-id="element-367" data-mp="3mm57v1a5ge1a">
                {selected && <span className="w-2 h-2 rounded-full bg-slate-900" data-id="element-368" data-mp="2b8t2un2nlpdj" />}
              </span>
              {opt}
            </button>;
      })}
      </div>;
  }
  return <textarea rows={4} value={value || ''} onChange={e => onChange(e.target.value)} placeholder="Share your feedback…" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900" data-id="element-369" data-mp="ls79ujqv5otm" />;
}
export function AssessmentRunner() {
  const initial = useScreenInit();
  const [step, setStep] = useState<number>(typeof initial.step === 'number' ? initial.step : 0);
  const [anonymous, setAnonymous] = useState<boolean>(initial.anonymous !== false);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [showHelp, setShowHelp] = useState(false);
  const isWelcome = step === 0;
  const isInstructions = step === 1;
  const isCompletion = step >= COMPLETION_STEP;
  const isQuestion = !isWelcome && !isInstructions && !isCompletion;
  const questionPageIndex = step - FIRST_QUESTION_STEP;
  const currentPage = isQuestion ? QUESTION_PAGES[questionPageIndex] : null;
  const answeredCount = Object.keys(answers).length;
  const totalQuestions = QUESTION_PAGES.reduce((n, p) => n + p.questions.length, 0);
  const progressPct = Math.min(100, Math.round(answeredCount / totalQuestions * 100));
  const next = () => setStep(s => Math.min(COMPLETION_STEP, s + 1));
  const back = () => setStep(s => Math.max(0, s - 1));
  const AnonBadge = () => <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-violet-50 text-violet-700 border border-violet-100" data-id="element-370" data-mp="3w2cuxjr8p8gu">
      <Shield className="h-3.5 w-3.5 mr-1.5" data-id="element-371" data-mp="9p4j5jjz4uzw" /> Anonymous
    </span>;
  return <div className="min-h-screen bg-slate-50 flex flex-col font-sans" data-id="element-372" data-mp="4pi4r6set8pi">
      {/* Top bar */}
      <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 sm:px-6 sticky top-0 z-20" data-id="element-373" data-mp="4ruo9odtfr4o">
        <div className="flex items-center" data-id="element-374" data-mp="eoh5m36xo01y">
          <div className="w-6 h-6 bg-slate-900 rounded-sm mr-3" data-id="element-375" data-mp="epiq0y9u9ywc" />
          <span className="font-semibold tracking-tight text-slate-900" data-id="element-376" data-mp="yaj1phhuyyo4">
            Oversight
          </span>
        </div>
        <div className="flex items-center gap-3" data-id="element-377" data-mp="12a9yc1u2g90k">
          {anonymous && <AnonBadge data-id="element-378" data-mp="oc3brhukhvg7" />}
          {isQuestion && <span className="hidden sm:inline-flex items-center text-xs font-medium text-slate-400" data-id="element-379" data-mp="odo2sxhoaw9b">
              <Cloud className="h-3.5 w-3.5 mr-1.5 text-emerald-500" data-id="element-380" data-mp="tc7t1a3auu7h" /> Saved
            </span>}
          <button onClick={() => setShowHelp(true)} className="text-slate-400 hover:text-slate-600" title="Help" data-id="element-381" data-mp="jc7335axdfub">
            <HelpCircle className="h-5 w-5" data-id="element-382" data-mp="5mdhcvupvbot" />
          </button>
        </div>
      </header>

      {/* Progress bar (questions only) */}
      {isQuestion && <div className="bg-white border-b border-slate-200 px-4 sm:px-6 py-3" data-id="element-383" data-mp="3rriia93qlmwl">
          <div className="max-w-2xl mx-auto" data-id="element-384" data-mp="lpn81b6yeqd">
            <div className="flex items-center justify-between text-xs font-medium text-slate-500 mb-2" data-id="element-385" data-mp="kectin8q8qa">
              <span data-id="element-386" data-mp="g377prdjifx5">
                {currentPage?.competency} · Section {questionPageIndex + 1} of{' '}
                {TOTAL_QUESTION_PAGES}
              </span>
              <span data-id="element-387" data-mp="72j1hiue1jut">{progressPct}% complete</span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2" data-id="element-388" data-mp="74vklzbvu09g">
              <div className="bg-slate-900 h-2 rounded-full transition-all duration-300" style={{
            width: `${progressPct}%`
          }} data-id="element-389" data-mp="23fwtziblfnd" />
            </div>
          </div>
        </div>}

      {/* Body */}
      <main className="flex-1 flex flex-col px-4 sm:px-6 py-8 sm:py-12" data-id="element-390" data-mp="3kj09ekgv1e6m">
        <div className="w-full max-w-2xl mx-auto flex-1" data-id="element-391" data-mp="3fjdwkc4y1zyc">
          {/* WELCOME */}
          {isWelcome && <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-8 sm:p-10 text-center animate-in fade-in duration-300" data-id="element-392" data-mp="3ak0zyzo9teik">
              <div className="mx-auto w-14 h-14 bg-slate-900 rounded-xl flex items-center justify-center mb-6" data-id="element-393" data-mp="35lg6rk7frv3m">
                <Star className="h-7 w-7 text-white" data-id="element-394" data-mp="2vj3vzc3ldj1o" />
              </div>
              <p className="text-sm font-medium text-slate-500 mb-2" data-id="element-395" data-mp="30e9k8hkgv9sx">
                You've been invited to provide feedback
              </p>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3" data-id="element-396" data-mp="3adqmep0if5k5">
                Q3 Leadership Review
              </h1>
              <p className="text-slate-500 mb-6" data-id="element-397" data-mp="2crkuj9p7ud1r">
                You're providing feedback for{' '}
                <strong className="text-slate-700" data-id="element-398" data-mp="2hq4krmaueazx">Sarah Connor</strong> as a{' '}
                <strong className="text-slate-700" data-id="element-399" data-mp="2mo6488c6kcbx">Peer</strong>. Your honest
                input helps support their growth.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3 mb-8 text-sm" data-id="element-400" data-mp="2rh8w8c1b7dq0">
                <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-slate-100 text-slate-600 font-medium" data-id="element-401" data-mp="1tzid3w4r2sqg">
                  <Clock className="h-4 w-4 mr-1.5" data-id="element-402" data-mp="3qpwu963e5ri6" /> ~5 minutes
                </span>
                <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-slate-100 text-slate-600 font-medium" data-id="element-403" data-mp="3qsaml76ospm1">
                  {totalQuestions} questions
                </span>
                {anonymous && <AnonBadge data-id="element-404" data-mp="8kbg1o5nz6sf" />}
              </div>

              <label className="flex items-center justify-center gap-2 text-sm text-slate-600 mb-8 cursor-pointer" data-id="element-405" data-mp="8j0eluzoyb4h">
                <input type="checkbox" checked={anonymous} onChange={e => setAnonymous(e.target.checked)} className="h-4 w-4 text-slate-900 focus:ring-slate-900 border-slate-300 rounded" data-id="element-406" data-mp="3819r4t4rzc78" />
                Submit my responses anonymously
              </label>

              <button onClick={next} className="w-full sm:w-auto bg-slate-900 text-white px-8 py-3 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors inline-flex items-center justify-center" data-id="element-407" data-mp="2p9gyiztmbt01">
                Get Started <ChevronRight className="h-4 w-4 ml-1" data-id="element-408" data-mp="2lonjjvjiw4yf" />
              </button>
            </div>}

          {/* INSTRUCTIONS */}
          {isInstructions && <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-8 sm:p-10 animate-in fade-in duration-300" data-id="element-409" data-mp="2cos43eixpnsl">
              <h1 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-410" data-mp="27tl2qdd50v6u">
                Before you begin
              </h1>
              <p className="text-slate-500 mb-8" data-id="element-411" data-mp="1y1p2owrhquzz">
                A few things to keep in mind as you complete this assessment.
              </p>

              <div className="space-y-5 mb-8" data-id="element-412" data-mp="1t2vof24dqzyr">
                <div className="flex gap-4" data-id="element-413" data-mp="1p10s6c1b3cru">
                  <div className="bg-slate-100 text-slate-700 w-8 h-8 rounded-lg flex items-center justify-center shrink-0 font-medium text-sm" data-id="element-414" data-mp="1p2btm57a48fs">
                    1
                  </div>
                  <div data-id="element-415" data-mp="1f3w0jwooyp3n">
                    <h3 className="text-sm font-semibold text-slate-900" data-id="element-416" data-mp="1f4nyvf9ob0xm">
                      Rate honestly
                    </h3>
                    <p className="text-sm text-slate-500 mt-0.5" data-id="element-417" data-mp="1adscoz0ysla2">
                      Use the full range of the scale. There are no right or
                      wrong answers.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4" data-id="element-418" data-mp="10ge4fmp732su">
                  <div className="bg-slate-100 text-slate-700 w-8 h-8 rounded-lg flex items-center justify-center shrink-0 font-medium text-sm" data-id="element-419" data-mp="1o5qdmmkivav5">
                    2
                  </div>
                  <div data-id="element-420" data-mp="1efn8jo04m6gg">
                    <h3 className="text-sm font-semibold text-slate-900" data-id="element-421" data-mp="1egf6v6l3yiaf">
                      Understand the scale
                    </h3>
                    <p className="text-sm text-slate-500 mt-0.5" data-id="element-422" data-mp="1jcx99zsyu4jl">
                      Most questions use a 5-point scale from{' '}
                      <strong data-id="element-423" data-mp="2bv18jnf6yb1d">Strongly Disagree</strong> to{' '}
                      <strong data-id="element-424" data-mp="2bwc8fc5imyn7">Strongly Agree</strong>. Some use a 1–10 scale or
                      open text.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4" data-id="element-425" data-mp="3rlk67jwe0ljs">
                  <div className="bg-slate-100 text-slate-700 w-8 h-8 rounded-lg flex items-center justify-center shrink-0 font-medium text-sm" data-id="element-426" data-mp="a5vmbmeqsjd9">
                    3
                  </div>
                  <div data-id="element-427" data-mp="594vq55ba5dl">
                    <h3 className="text-sm font-semibold text-slate-900" data-id="element-428" data-mp="3dmc86ubynjh6">
                      Your progress is saved
                    </h3>
                    <p className="text-sm text-slate-500 mt-0.5" data-id="element-429" data-mp="38p26ogbarhcz">
                      You can leave and return anytime — your answers are saved
                      automatically.
                    </p>
                  </div>
                </div>
              </div>

              <div className={`flex items-start gap-3 rounded-lg p-4 mb-8 border ${anonymous ? 'bg-violet-50 border-violet-100' : 'bg-slate-50 border-slate-200'}`} data-id="element-430" data-mp="11w9t6yoc8vff">
                <Lock className={`h-5 w-5 shrink-0 mt-0.5 ${anonymous ? 'text-violet-600' : 'text-slate-500'}`} data-id="element-431" data-mp="1t6ebz17soa5q" />
                <p className="text-sm text-slate-600" data-id="element-432" data-mp="1x9any92tx80p">
                  {anonymous ? 'This assessment is anonymous. Your individual responses will be combined with others and the subject will never see who said what.' : 'Your name will be visible with your responses to the assessment administrator.'}
                </p>
              </div>

              <div className="flex items-center justify-between" data-id="element-433" data-mp="2g4iyre2yigdj">
                <button onClick={back} className="text-sm font-medium text-slate-600 hover:text-slate-900 inline-flex items-center" data-id="element-434" data-mp="2kwitms466j39">
                  <ChevronLeft className="h-4 w-4 mr-1" data-id="element-435" data-mp="2uvi2obsukgqs" /> Back
                </button>
                <button onClick={next} className="bg-slate-900 text-white px-6 py-2.5 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors inline-flex items-center" data-id="element-436" data-mp="2zttdaoqyuva2">
                  Begin Assessment <ChevronRight className="h-4 w-4 ml-1" data-id="element-437" data-mp="j61ua0ody7bd" />
                </button>
              </div>
            </div>}

          {/* QUESTION PAGES */}
          {isQuestion && currentPage && <div className="animate-in fade-in duration-300 space-y-6" data-id="element-438" data-mp="9b9wowuszkgl">
              {currentPage.questions.map((q, i) => <div key={q.id} className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 sm:p-8" data-id="element-439" data-mp="11y4efm08k5qd" data-mpi={i}>
                  <div className="flex items-start gap-3 mb-5" data-id="element-440" data-mp="x5tewk9eqlyc">
                    <span className="text-xs font-semibold text-slate-400 mt-1 shrink-0" data-id="element-441" data-mp="s930009c9wei">
                      Q{questionPageIndex * 2 + i + 1}
                    </span>
                    <h2 className="text-base sm:text-lg font-medium text-slate-900" data-id="element-442" data-mp="1kwyucybfrc84">
                      {q.text}
                    </h2>
                  </div>
                  <QuestionInput q={q} value={answers[q.id]} onChange={v => setAnswers(a => ({
              ...a,
              [q.id]: v
            }))} data-id="element-443" data-mp="3f5m5v5azlzqi" />
                </div>)}

              <div className="flex items-center justify-between pt-2" data-id="element-444" data-mp="2wdtbb6g9s6go">
                <button onClick={back} className="px-4 py-2.5 border border-slate-300 rounded-md text-sm font-medium text-slate-700 hover:bg-white bg-white transition-colors inline-flex items-center" data-id="element-445" data-mp="2wgpa0wzxyz66">
                  <ChevronLeft className="h-4 w-4 mr-1" data-id="element-446" data-mp="2rg199jlwv3yr" /> Previous
                </button>
                <div className="flex items-center gap-3" data-id="element-447" data-mp="kptrsackk3r1">
                  <button className="hidden sm:inline text-sm font-medium text-slate-500 hover:text-slate-900" data-id="element-448" data-mp="pnv66hz1vew6">
                    Save & exit
                  </button>
                  <button onClick={next} className="bg-slate-900 text-white px-6 py-2.5 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors inline-flex items-center" data-id="element-449" data-mp="2a6740ap48euh">
                    {questionPageIndex === TOTAL_QUESTION_PAGES - 1 ? 'Submit' : 'Next'}
                    <ChevronRight className="h-4 w-4 ml-1" data-id="element-450" data-mp="2sv3xwdglp5c6" />
                  </button>
                </div>
              </div>
            </div>}

          {/* COMPLETION */}
          {isCompletion && <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-8 sm:p-12 text-center animate-in fade-in zoom-in-95 duration-500" data-id="element-451" data-mp="32ttjohbmb9xp">
              <div className="mx-auto w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-6" data-id="element-452" data-mp="18g21uuj4xjr6">
                <CheckCircle2 className="h-8 w-8 text-emerald-600" data-id="element-453" data-mp="13iiorxy6bd0t" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3" data-id="element-454" data-mp="hp39blfq2not">
                Thank you!
              </h1>
              <p className="text-slate-500 max-w-md mx-auto mb-6" data-id="element-455" data-mp="mltn22421772">
                Your feedback for{' '}
                <strong className="text-slate-700" data-id="element-456" data-mp="rempgjc1w3fz">Sarah Connor</strong> has
                been submitted successfully.
                {anonymous && ' Your responses remain completely anonymous.'}
              </p>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-left max-w-md mx-auto mb-8" data-id="element-457" data-mp="3uwxzrfjr1dsq">
                <h3 className="text-sm font-semibold text-slate-900 mb-1" data-id="element-458" data-mp="8nmwl71fq9hf">
                  What happens next?
                </h3>
                <p className="text-sm text-slate-500" data-id="element-459" data-mp="cpgtd48ietcb">
                  Once all respondents have completed their feedback, results
                  are aggregated into a confidential report and shared with the
                  subject and their manager.
                </p>
              </div>
              <button className="text-sm font-medium text-slate-600 hover:text-slate-900" data-id="element-460" data-mp="38mo68k14vwrp">
                You may now close this window.
              </button>
            </div>}
        </div>
      </main>

      {/* Help modal */}
      {showHelp && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-461" data-mp="2zrrpn6f33htt">
          <div className="absolute inset-0 bg-slate-900/30 animate-in fade-in" onClick={() => setShowHelp(false)} data-id="element-462" data-mp="2usx6u4h49t9p" />
          <div className="relative w-full max-w-sm bg-white rounded-xl shadow-xl p-6 animate-in zoom-in-95 duration-200" data-id="element-463" data-mp="2kwkbz39u59dd">
            <div className="flex items-center justify-between mb-3" data-id="element-464" data-mp="2gw1lpdnlcbda">
              <h2 className="text-base font-semibold text-slate-900" data-id="element-465" data-mp="2bwp0excdbcct">
                Need help?
              </h2>
              <button onClick={() => setShowHelp(false)} className="text-slate-400 hover:text-slate-600" data-id="element-466" data-mp="f2u60342frqv">
                <X className="h-5 w-5" data-id="element-467" data-mp="13fdiyuuz6hy2" />
              </button>
            </div>
            <p className="text-sm text-slate-500 mb-4" data-id="element-468" data-mp="13hg6974qtwz3">
              Your progress is saved automatically. You can close this window
              and return via your invitation link at any time.
            </p>
            {anonymous && <div className="flex items-start gap-2 text-sm text-violet-700 bg-violet-50 border border-violet-100 rounded-md p-3" data-id="element-469" data-mp="oqz7jvjhtl5w">
                <Shield className="h-4 w-4 shrink-0 mt-0.5" data-id="element-470" data-mp="jsxoham2hluf" /> Your responses
                are anonymous.
              </div>}
          </div>
        </div>}
    </div>;
}